// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Interfaces
import {
  ICertificationWebpageRelevantProofGetListAttrs,
  ICertificationWebpageRelevantProofUpdateAttrs,
  ICertificationWebpageRelevantProofUploadDocumentAttrs,
  ICertificationWebpageRelevantProofDeleteDocumentAttrs,
  ICertificationWebpageRelevantProofDownloadDocumentAttrs,
} from 'interfaces/webpage/certification/registration/precondition'

const useCertificationRelevantProof = () => {
  // Hook
  const {
    certificationWebpageRelevantProof: {
      certificationWebpageRelevantProof_loading,
      certificationWebpageRelevantProof_list,
      certificationWebpageRelevantProof_CLEAR_LIST,
      certificationWebpageRelevantProof_MAP_ANSWER_FILE_LIST,
      certificationWebpageRelevantProof_fetchList: fetchList,
      certificationWebpageRelevantProof_update: update,
      certificationWebpageRelevantProof_uploadDocument: uploadDocument,
      certificationWebpageRelevantProof_deleteDocument: deleteDocument,
      certificationWebpageRelevantProof_downloadDocument: downloadDocument,
    },
  } = useStores()

  /**
   * @description Fetch relevant proof list
   *
   * @param {ICertificationWebpageRelevantProofGetListAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageRelevantProof_fetchList = useCallback(
    (payload: ICertificationWebpageRelevantProofGetListAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        fetchList({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [fetchList],
  )

  /**
   * @description Update relevant proof
   *
   * @param {ICertificationWebpageRelevantProofUpdateAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageRelevantProof_update = useCallback(
    (payload: ICertificationWebpageRelevantProofUpdateAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        update({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          body: { ...payload.body },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [update],
  )

  /**
   * @description Upload relevant proof document
   *
   * @param {ICertificationWebpageRelevantProofUploadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageRelevantProof_uploadDocument = useCallback(
    (payload: ICertificationWebpageRelevantProofUploadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        uploadDocument({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          body: { ...payload.body },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [uploadDocument],
  )

  /**
   * @description Delete relevant proof document
   *
   * @param {ICertificationWebpageRelevantProofDeleteDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageRelevantProof_deleteDocument = useCallback(
    (payload: ICertificationWebpageRelevantProofDeleteDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        deleteDocument({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [deleteDocument],
  )

  /**
   * @description Download document of relevant proof
   *
   * @param {ICertificationWebpageRelevantProofDownloadDocumentAttrs} payload
   *
   * @return {Promise<void>} Promise<void>
   */
  const certificationWebpageRelevantProof_downloadDocument = useCallback(
    (payload: ICertificationWebpageRelevantProofDownloadDocumentAttrs): Promise<void> => {
      return new Promise((resolve, reject) => {
        downloadDocument({
          params: { ...payload.params },
          requestConfig: {
            ...payload.requestConfig,
          },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [downloadDocument],
  )

  return {
    certificationWebpageRelevantProof_loading,
    certificationWebpageRelevantProof_list,
    certificationWebpageRelevantProof_CLEAR_LIST,
    certificationWebpageRelevantProof_MAP_ANSWER_FILE_LIST,
    certificationWebpageRelevantProof_fetchList,
    certificationWebpageRelevantProof_update,
    certificationWebpageRelevantProof_uploadDocument,
    certificationWebpageRelevantProof_deleteDocument,
    certificationWebpageRelevantProof_downloadDocument,
  }
}

export { useCertificationRelevantProof }
